import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Archive, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { listAdminSharedSessions, hideSharedSession, deleteSharedSession, downloadSharedSessionFull } from '../api';
import SharedSessionCard from '../components/SharedSessionCard';
import { downloadJson } from '../utils/downloadJson';
import { groupByDay } from '../utils/groupByDay';
import { useOrg } from '../contexts/OrgContext';

const PER_PAGE = 20;

/** Org-wide moderation view — every session anyone in the org has pushed to
 *  the registry, including shares another uploader has hidden (soft-hidden
 *  shares are invisible on the personal "Shared Sessions" page except to
 *  their own uploader; here an admin sees everything, flagged). */
export default function SessionRegistry() {
  const { active } = useOrg();
  const orgId = active?.org_id;
  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [msg, setMsg] = useState(null);

  // Requests can resolve out of order (a slow response for an org/search
  // that's since been navigated away from landing after a faster, current
  // one) — `clearTimeout` in the debounce effect below only cancels a timer
  // that hasn't fired yet, not a fetch already in flight. Each caller claims
  // the next sequence number *before* the debounce delay, not when the
  // delayed fetch actually starts — otherwise an old request already in
  // flight could still resolve and pass the staleness check during that
  // window, since the counter wouldn't have moved yet. A response only
  // commits if its claimed number is still the most recent by the time it
  // resolves; a stale one is silently dropped instead of overwriting the
  // current, correct results.
  const requestSeq = useRef(0);

  // Returns the fetched total (or null on failure/staleness) so callers that
  // need to react to the post-load count — see `withBusy`'s page-clamp below
  // — don't have to duplicate the request. `seq` must be claimed by the
  // caller (`++requestSeq.current`) at the moment intent changes, not inside
  // this function — see the comment on `requestSeq` above.
  const load = useCallback(async (q, p, seq) => {
    try {
      const r = await listAdminSharedSessions(q, p, PER_PAGE);
      if (seq !== requestSeq.current) return null;
      setSessions(r.data.sessions || []);
      setTotal(r.data.total || 0);
      setMsg(null);
      return r.data.total || 0;
    } catch (e) {
      if (seq !== requestSeq.current) return null;
      // Surfaced, not swallowed — otherwise a failed load is indistinguishable
      // from "this org genuinely has zero shared sessions."
      setMsg({ kind: 'err', text: e.response?.data?.detail || 'Failed to load shared sessions' });
      return null;
    }
  }, []);

  // `orgId` isn't read inside `load` (the org is scoped via the `X-Org-Id`
  // header on every request), but it needs to be a dependency here anyway —
  // switching orgs via the header switcher otherwise leaves this org's stale
  // list on screen (and a hide/delete click would then act on a reg_id under
  // the NEW org's header, a confusing 403/404 at best).
  const firstLoad = useRef(true);
  useEffect(() => {
    const seq = ++requestSeq.current;
    const isFirst = firstLoad.current;
    const t = setTimeout(async () => {
      firstLoad.current = false;
      await load(search, page, seq);
      if (isFirst) setLoading(false);
    }, isFirst ? 0 : 250); // no debounce delay on the very first load
    return () => clearTimeout(t);
  }, [search, page, orgId, load]);

  // A new org's session list starts back at page 1, same reasoning as a new
  // search term.
  useEffect(() => {
    setPage(1);
  }, [orgId]);

  const withBusy = async (regId, fn) => {
    setBusyId(regId); setMsg(null);
    try {
      await fn();
      const newTotal = await load(search, page, ++requestSeq.current);
      if (newTotal !== null) {
        // Deleting the last item on a page beyond page 1 can leave `page`
        // pointing past the now-shrunk result set. Clamp and re-fetch
        // immediately rather than rendering a false "no results".
        const maxPage = Math.max(1, Math.ceil(newTotal / PER_PAGE));
        if (page > maxPage) {
          setPage(maxPage);
          await load(search, maxPage, ++requestSeq.current);
        }
      }
    } catch (e) {
      setMsg({ kind: 'err', text: e.response?.data?.detail || 'Action failed' });
    } finally { setBusyId(null); }
  };

  const handleHide = (s) => withBusy(s.reg_id, () => hideSharedSession(s.reg_id, !s.hidden_by_admin));
  const handleDelete = (s) => {
    if (!window.confirm(`Delete "${s.title || 'this shared session'}"? This can't be undone.`)) return;
    return withBusy(s.reg_id, () => deleteSharedSession(s.reg_id));
  };
  const handleDownload = async (s) => {
    try {
      const r = await downloadSharedSessionFull(s.reg_id);
      downloadJson(`${s.reg_id}.json`, r.data.full_json);
    } catch (e) {
      setMsg({ kind: 'err', text: e.response?.data?.detail || 'Download failed' });
    }
  };

  if (loading) return <div className="text-center text-fg-muted py-16">Loading…</div>;

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const groups = groupByDay(sessions, 'updated_at');

  return (
    <div className="w-full">
      <div className="mb-6">
        <h1 className="font-display text-2xl font-bold text-fg flex items-center gap-2">
          <Archive className="w-6 h-6 text-orange" /> Session Registry
        </h1>
        <p className="text-fg-muted text-sm mt-1">
          Every session shared to this org, from any member — sanitized (PII, secrets, local paths) before storage.
          Hide a share from teammates, or delete it outright.
        </p>
      </div>

      {msg && (
        <div className="mb-4 p-3 rounded-lg border text-sm bg-red-500/10 border-red-500/25 text-red-400">
          {msg.text}
        </div>
      )}

      <div className="relative mb-4">
        <Search className="w-4 h-4 text-fg-muted absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search by title, summary, or uploader…"
          className="w-full pl-9 pr-3 py-2.5 bg-field border border-field-border rounded-lg text-fg text-sm"
        />
      </div>

      {sessions.length === 0 ? (
        // A failed load already has its own message in the error banner
        // above — showing "no sessions yet" underneath it would read as two
        // conflicting explanations for the same empty list.
        msg?.kind !== 'err' && (
          <div className="bg-panel border border-panel-border rounded-xl p-8 text-center">
            <p className="text-sm text-fg-muted">
              {search ? 'No shared sessions match your search.' : 'No shared sessions in this organization yet.'}
            </p>
          </div>
        )
      ) : (
        <>
          <div className="space-y-6">
            {groups.map((g) => (
              <div key={g.key}>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-fg-muted">{g.label}</span>
                  <span className="h-px flex-1 bg-panel-border" />
                  <span className="text-xs text-fg-muted tabular-nums">{g.items.length}</span>
                </div>
                <div className="space-y-3">
                  {g.items.map((s) => (
                    <SharedSessionCard key={s.reg_id} s={s} busy={busyId === s.reg_id} canModerate={true}
                      onHide={handleHide} onDelete={handleDelete} onDownload={handleDownload} />
                  ))}
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                className="flex items-center gap-1 px-3 py-1.5 bg-field border border-field-border text-fg rounded-lg text-sm hover:border-orange disabled:opacity-40 disabled:hover:border-field-border transition-colors">
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
              <span className="text-xs text-fg-muted tabular-nums">Page {page} of {totalPages}</span>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                className="flex items-center gap-1 px-3 py-1.5 bg-field border border-field-border text-fg rounded-lg text-sm hover:border-orange disabled:opacity-40 disabled:hover:border-field-border transition-colors">
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
